import { convertToNumberKWD } from './common';

/**
 * Transform catering packages data into the API payload format for add-to-cart
 * @param {Object} params - Parameters for the transformation
 * @param {number} params.customerId - Customer ID
 * @param {number} params.companyId - Company ID
 * @param {number} params.addressId - Address ID
 * @param {Array} params.packages - Array of catering packages
 * @param {Object} params.filters - Filters containing pax, date, time
 * @param {string} params.notes - Special instructions
 * @param {number} params.restaurantId - Restaurant ID
 * @param {number} params.branchId - Branch ID (default 0 for catering)
 * @param {number} params.countryId - Country ID (default 3 for Kuwait)
 * @returns {Object} API payload for catering add-to-cart
 */
export const transformCateringCartPayload = ({
  customerId,
  companyId,
  addressId,
  packages,
  filters,
  notes,
  restaurantId,
  branchId = 0,
  countryId = 3
}) => {
  // Get the first package to extract catering_id and calculate total price
  const firstPackage = packages[0];
  if (!firstPackage) {
    throw new Error('At least one package is required');
  }

  // Calculate total catering price across all packages
  const totalPrice = packages.reduce((total, pkg) => {
    const packagePrice = calculatePackagePrice(pkg, filters.pax);
    return total + packagePrice;
  }, 0);
  
  // Add chargeable items cost
  const chargeableItemsCost = calculateTotalChargeableItemsCost(packages, filters.pax);
  const totalPriceWithChargeableItems = totalPrice + chargeableItemsCost;

  // Transform items to the new catering service items format
  const cateringServiceItems = packages.flatMap(pkg => {
    if (!pkg.items || !Array.isArray(pkg.items)) return [];
    
    // Build a map of items by category to track order and determine which are chargeable
    const itemsByCategory = {};
    const itemDetails = []; // Store item details with their position
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      
      if (item.has_variants && item.items) {
        item.items.forEach(variantItem => {
          const addOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          
          addOrderHistory.forEach(order => {
            const itemPrice = calculateVariantItemPrice(item, variantItem);
            itemsByCategory[categoryId].push({ order, price: itemPrice });
            itemDetails.push({
              categoryId,
              item,
              variantItem,
              order,
              price: itemPrice,
              isVariant: true
            });
          });
        });
      } else {
        const addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
        
        addOrderHistory.forEach(order => {
          const itemPrice = getCateringItemPrice(item, filters.pax);
          itemsByCategory[categoryId].push({ order, price: itemPrice });
          itemDetails.push({
            categoryId,
            item,
            order,
            price: itemPrice,
            isVariant: false
          });
        });
      }
    });
    
    // For each category, determine which items are chargeable
    const chargeableMap = {}; // Map to track which items at which order positions are chargeable
    
    Object.keys(itemsByCategory).forEach(categoryId => {
      const categoryItems = itemsByCategory[categoryId];
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      
      let maxFreeItems = 0;
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => filters.pax >= parseInt(rule.pax));
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItems = matchingRule.choose;
        }
      }
      
      // Sort items by order (temporal sequence)
      categoryItems.sort((a, b) => a.order - b.order);
      
      // Items beyond maxFreeItems are chargeable
      chargeableMap[categoryId] = new Set(
        categoryItems.slice(maxFreeItems).map(item => item.order)
      );
    });
    
    // Now create the API payload items
    return pkg.items.map(item => {
      // Find the category for this item
      const categoryId = item.category_id || item.catering_category_id || "1";
      
      // Find the category limit to get the correct "choose" value
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      let chooseValue = "1"; // Default fallback
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        // Find the appropriate choose rule for current PAX
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          filters.pax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          chooseValue = matchingRule.choose.toString();
        }
      }
      
      // Determine portion based on portion_by_pax array
      const getPortionForPax = (item, currentPax) => {
        if (item.portion_by_pax && Array.isArray(item.portion_by_pax) && item.portion_by_pax.length > 0) {
          // Sort by pax descending to find the highest applicable tier
          const sortedPortions = [...item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
          
          // Find the appropriate portion for current PAX
          const matchingPortion = sortedPortions.find(p => currentPax >= parseInt(p.pax));
          
          if (matchingPortion && matchingPortion.portions) {
            return matchingPortion.portions.toString();
          }
          
          // If no match, use the lowest tier (first in original array)
          if (item.portion_by_pax[0] && item.portion_by_pax[0].portions) {
            return item.portion_by_pax[0].portions.toString();
          }
        }
        
        // Default fallback
        return "10";
      };
      
      const portionValue = getPortionForPax(item, filters.pax);
      
      if (item.has_variants && item.items) {
        // For variant items, create entries for each variant
        return item.items.map(variantItem => {
          const itemPrice = calculateVariantItemPrice(item, variantItem);
          const addOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          
          // Calculate add_on_price: sum of prices for chargeable instances only
          let totalAddOnPrice = 0;
          addOrderHistory.forEach(order => {
            if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
              totalAddOnPrice += itemPrice;
            }
          });
          
          return {
            catering_category_id: categoryId.toString(),
            catering_item_id: item.id.toString(),
            quantity: variantItem.quantity?.toString() || "1",
            choose: chooseValue,
            portion: portionValue,
            no_of_pax: filters.pax?.toString() || "20",
            add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "0.000",
            currency: "KWD"
          };
        });
      } else {
        // For non-variant items
        const itemPrice = getCateringItemPrice(item, filters.pax);
        const addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
        
        // Calculate add_on_price: sum of prices for chargeable instances only
        let totalAddOnPrice = 0;
        addOrderHistory.forEach(order => {
          if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
            totalAddOnPrice += itemPrice;
          }
        });
        
        return {
          catering_category_id: categoryId.toString(),
          catering_item_id: item.id.toString(),
          quantity: item.quantity?.toString() || "1",
          choose: chooseValue,
          portion: portionValue,
          no_of_pax: filters.pax?.toString() || "20",
          add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "0.000",
          currency: "KWD"
        };
      }
    });
  }).flat();

  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Parse time slot from filters
  const timeSlot = filters.time || "02:00 PM - 03:30 PM";

  return {
    country_id: countryId,
    catering_id: firstPackage.id,
    restaurant_id: restaurantId,
    customer_id: customerId,
    company_id: companyId,
    address_id: addressId,
    no_of_pax: filters.pax,
    quantity: 1, // Default quantity for the catering service
    service_item_price: totalPriceWithChargeableItems.toString(),
    currency: "KWD",
    service_item_date: formatDate(filters.date),
    service_item_slot: timeSlot,
    is_discount_applied: "",
    promo_code_type: "",
    promo_code: "",
    discount_price: "",
    special_instructions_1: notes || "",
    special_instructions_2: "", // Additional instructions if needed
    catering_service_items: cateringServiceItems
  };
};

/**
 * Transform backend cart data to frontend structure for checkoutCart
 * @param {Object} backendCartData - Cart data from backend API
 * @returns {Array} Array of packages in frontend format
 */
export const transformBackendCartToFrontend = (backendCartData) => {
  if (!backendCartData || !Array.isArray(backendCartData)) {
    return [];
  }

  return backendCartData.map(cartItem => {
    // Transform backend cart item to frontend package structure
    const packageData = {
      id: cartItem.catering_id || cartItem.id,
      name_en: cartItem.package_name_en || cartItem.name_en,
      title: cartItem.package_name_en || cartItem.name_en,
      description_en: cartItem.package_description_en || cartItem.description_en,
      image1: cartItem.package_image || cartItem.image,
      basePrice: parseFloat(cartItem.catering_price?.replace('KWD ', '') || '0'),
      no_of_pax: cartItem.no_of_pax || 20,
      duration_from: cartItem.duration_from || "01:00 PM",
      duration_to: cartItem.duration_to || "04:00 PM",
      special_instructions: cartItem.special_instructions || "",
      items: []
    };

    // Transform catering items if they exist
    if (cartItem.catering_items && Array.isArray(cartItem.catering_items)) {
      packageData.items = cartItem.catering_items.map(item => {
        const transformedItem = {
          id: item.item_id,
          name_en: item.item_name_en || item.name_en,
          name: item.item_name_en || item.name_en,
          quantity: item.quantity || 1,
          price: item.item_price || "0.000",
          has_variants: false,
          items: [] // For variant items
        };

        // Check if item has variants
        if (item.size_variant && item.size_variant.length > 0 ||
            item.topping_variant && item.topping_variant.length > 0 ||
            item.custom_variant && item.custom_variant.length > 0 ||
            item.side_variant && item.side_variant.length > 0) {
          
          transformedItem.has_variants = true;
          
          // Create a variant item entry
          const variantItem = {
            variantId: `${item.item_id}_${Date.now()}`, // Generate unique variant ID
            quantity: item.quantity || 1,
            selectedVariant: []
          };

          // Add size variants
          if (item.size_variant && item.size_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'size',
              options: item.size_variant
            });
          }

          // Add topping variants
          if (item.topping_variant && item.topping_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'topping',
              options: item.topping_variant
            });
          }

          // Add custom variants
          if (item.custom_variant && item.custom_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'custom',
              options: item.custom_variant
            });
          }

          // Add side variants
          if (item.side_variant && item.side_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'side',
              options: item.side_variant
            });
          }

          transformedItem.items = [variantItem];
        }

        return transformedItem;
      });
    }

    return packageData;
  });
};

/**
 * Get the price of a catering item based on its add_on_options and current PAX
 * @param {Object} item - The catering item with add_on_options
 * @param {number} currentPax - Current PAX count
 * @returns {number} Price for the current PAX
 */
export const getCateringItemPrice = (item, currentPax) => {
  // Check if item has add_on_options (catering items)
  if (item.add_on_options && Array.isArray(item.add_on_options) && item.add_on_options.length > 0) {
    // Find the matching price tier based on current PAX
    const matchingOption = item.add_on_options.find(option => {
      const paxFrom = parseInt(option.pax_from || 0);
      const paxTo = parseInt(option.pax_to || 999);
      return currentPax >= paxFrom && currentPax <= paxTo;
    });

    if (matchingOption && matchingOption.price) {
      return parseFloat(matchingOption.price) || 0;
    }

    // Fallback to first option if no match
    if (item.add_on_options[0] && item.add_on_options[0].price) {
      return parseFloat(item.add_on_options[0].price) || 0;
    }
  }

  // Fallback to item.price if it exists
  if (item.price) {
    return convertToNumberKWD(item.price);
  }

  return 0;
};

/**
 * Calculate variant item price including base price and options
 * @param {Object} item - The menu item object
 * @param {Object} variantItem - The variant item with selected options
 * @returns {number} Total price (base + options)
 */
export const calculateVariantItemPrice = (item, variantItem) => {
  // Get base price of the item
  const basePrice = convertToNumberKWD(item.price);

  // Check if selectedVariant exists and is an array
  if (!variantItem.selectedVariant || !Array.isArray(variantItem.selectedVariant) || variantItem.selectedVariant.length === 0) {
    console.warn('Invalid or empty selectedVariant for variant item:', {
      item: item.name || item.name_en,
      itemId: item.id,
      itemPrice: item.price,
      basePrice: basePrice,
      variantItem: variantItem,
      selectedVariant: variantItem.selectedVariant
    });
    // Return just the base price if no valid variant selections
    if (isNaN(basePrice)) {
      console.error('Base price is NaN for item:', item);
      return 0; // Ultimate fallback
    }
    return basePrice;
  }

  // Calculate options price
  const optionsPrice = variantItem.selectedVariant.reduce(
    (total, variant) => {
      // Check if variant has options array
      if (!variant.options || !Array.isArray(variant.options)) {
        console.warn('Variant missing options array:', variant);
        return total;
      }
      
      const optionsTotal = variant.options.reduce((optTotal, opt) => {
        return optTotal + (opt.unit_price || 0) * (opt.option_quantity || 0);
      }, 0);
      return total + optionsTotal;
    },
    0
  );

  // Return total price (base price + options price)
  return basePrice + optionsPrice;
};

/**
 * Calculate chargeable items based on pax_limit logic
 * @param {Array} items - Array of items to calculate
 * @param {number} currentPax - Current PAX count
 * @param {Array} paxLimits - Array of pax limit rules for categories
 * @returns {Array} Items with chargeable quantities calculated
 */
export const calculateChargeableItems = (items, currentPax, paxLimits) => {
  if (!Array.isArray(items) || !Array.isArray(paxLimits) || !currentPax) {
    return items || [];
  }

  return items.map(item => {
    // Find the pax limit for this item's category
    const categoryLimit = paxLimits.find(limit => limit.category_id === item.category_id);

    if (!categoryLimit || categoryLimit.item_limit === null || categoryLimit.pax_limit === null) {
      // No limit defined for this category, item is fully chargeable
      return { 
        ...item, 
        chargeableQuantity: item.quantity || 0,
        freeQuantity: 0,
        isChargeablePerItem: true // All items in this category are chargeable
      };
    }

    // Calculate how many items are free based on pax
    const maxFreePax = categoryLimit.pax_limit;
    const itemsPerPax = categoryLimit.item_limit;

    // Calculate free items: (current_pax / max_free_pax) * items_per_pax
    const freeItems = Math.floor((currentPax / maxFreePax) * itemsPerPax);

    // Chargeable quantity is total minus free items (minimum 0)
    const chargeableQuantity = Math.max(0, (item.quantity || 0) - freeItems);
    const freeQuantity = (item.quantity || 0) - chargeableQuantity;

    return {
      ...item,
      chargeableQuantity,
      freeQuantity,
      isChargeablePerItem: chargeableQuantity > 0, // Flag to show if this item has chargeable quantities
      categoryLimit // Store the limit info for display purposes
    };
  });
};

/**
 * Calculate display price for individual items based on pax limits
 * @param {Object} item - The menu item
 * @param {Array} paxLimits - Array of pax limit rules
 * @param {number} currentPax - Current PAX count
 * @param {Array} allItemsInCategory - All items in the same category for total calculation
 * @returns {number} Display price (0 if free, full price if chargeable)
 */
export const calculateItemDisplayPrice = (item, paxLimits, currentPax, allItemsInCategory = []) => {
  // Find the pax limit for this item's category
  const categoryLimit = paxLimits.find(limit => limit.category_id === item.category_id);
  
  if (!categoryLimit || categoryLimit.item_limit === null || categoryLimit.pax_limit === null) {
    // No limit defined for this category, show full price
    return getCateringItemPrice(item, currentPax);
  }

  // Calculate how many items are free based on pax
  const maxFreePax = categoryLimit.pax_limit;
  const itemsPerPax = categoryLimit.item_limit;
  const freeItems = Math.floor((currentPax / maxFreePax) * itemsPerPax);
  
  // Count total items in this category across all items in the package
  const itemsInSameCategory = allItemsInCategory.filter(itm => itm.category_id === item.category_id);
  const totalCategoryQuantity = itemsInSameCategory.reduce((sum, itm) => sum + (itm.quantity || 0), 0);
  
  // If all items in this category are within the free limit, show 0
  if (totalCategoryQuantity <= freeItems) {
    return 0;
  }
  
  // For simplicity, if any item in the category exceeds the limit, show full price for all
  // In a more complex implementation, you might want to track which specific items are free
  return getCateringItemPrice(item, currentPax);
};

/**
 * Calculate total chargeable items cost across all packages
 * @param {Array} packages - Array of packages with items
 * @param {number} currentPax - Current PAX count
 * @returns {number} Total cost of chargeable items
 */
export const calculateTotalChargeableItemsCost = (packages, currentPax) => {
  if (!packages || packages.length === 0) return 0;

  return packages.reduce((packageTotal, pkg) => {
    if (!pkg.items || !Array.isArray(pkg.items)) return packageTotal;

    console.log('Calculating chargeable items cost for package:', {
      packageId: pkg.id,
      packageName: pkg.name_en || pkg.title,
      currentPax: currentPax,
      totalItems: pkg.items.length
    });

    // Group items by category to calculate category-level free items
    const itemsByCategory = {};
    
    // First, organize all items by category
    pkg.items.forEach(item => {
      const categoryId = item.category_id;
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      itemsByCategory[categoryId].push(item);
    });

    // Calculate cost for each category
    const packageItemsCost = Object.keys(itemsByCategory).reduce((categoryTotal, categoryId) => {
      const categoryItems = itemsByCategory[categoryId];
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      
      let maxFreeItemsForCategory = 0;
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        // Find the appropriate choose rule for current PAX
        // Sort rules by pax descending and find the first one where currentPax >= rule.pax
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          currentPax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItemsForCategory = matchingRule.choose;
        }
      }

      // Calculate total quantity in this category
      let totalCategoryQuantity = 0;
      // Store item prices with their add order for proper temporal sorting
      const itemPricesWithOrder = [];
      console.log('Category items:', categoryItems);
      categoryItems.forEach(item => {
        if (item?.has_variants) {
          // For variant items, sum up all variant quantities
          item.items?.forEach(variantItem => {
            totalCategoryQuantity += variantItem.quantity || 0;
            const variantPrice = calculateVariantItemPrice(item, variantItem);
            
            // If variant has addOrderHistory, use it; otherwise use sequential order
            const addOrderHistory = variantItem.addOrderHistory || 
              Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
            
            addOrderHistory.forEach(order => {
              itemPricesWithOrder.push({ price: variantPrice, order });
            });
          });
        } else {
          // For catering items (with add_on_options) or regular items
          totalCategoryQuantity += item.quantity || 0;
          // Use getCateringItemPrice which handles both add_on_options and regular price
          const itemPrice = getCateringItemPrice(item, currentPax);
          
          // Use addOrderHistory to track when each instance was added
          const addOrderHistory = item.addOrderHistory || 
            Array.from({ length: item.quantity || 0 }, (_, i) => i);
          
          addOrderHistory.forEach(order => {
            itemPricesWithOrder.push({ price: itemPrice, order });
          });
        }
      });
      
      // Sort by add order (temporal sequence) to maintain the actual addition order
      itemPricesWithOrder.sort((a, b) => a.order - b.order);
      const itemPrices = itemPricesWithOrder.map(item => item.price);

      // Calculate chargeable quantity for this category
      const chargeableCategoryQuantity = Math.max(0, totalCategoryQuantity - maxFreeItemsForCategory);
      
      console.log('Category calculation:', {
        categoryId: categoryId,
        totalCategoryQuantity: totalCategoryQuantity,
        maxFreeItemsForCategory: maxFreeItemsForCategory,
        chargeableCategoryQuantity: chargeableCategoryQuantity,
        itemPricesCount: itemPrices.length
      });
      
      if (chargeableCategoryQuantity === 0) {
        console.log('All items in category', categoryId, 'are free');
        return categoryTotal; // All items in this category are free
      }

      // First N items (based on limit) are FREE, remaining items are CHARGEABLE
      // Charge for items that exceed the category limit in the order they were added
      const chargeablePrices = itemPrices.slice(maxFreeItemsForCategory);
      const categoryCost = chargeablePrices.reduce((sum, price) => sum + price, 0);

      console.log('Category cost calculation:', {
        categoryId: categoryId,
        chargeablePrices: chargeablePrices,
        categoryCost: categoryCost
      });

      return categoryTotal + categoryCost;
    }, 0);

    return packageTotal + packageItemsCost;
  }, 0);
};

/**
 * Calculate package price based on PAX count
 * @param {Object} pkg - Package object with pricing_tiers
 * @param {number} currentPax - Current PAX count
 * @returns {number} Package price for the current PAX count
 */
export const calculatePackagePrice = (pkg, currentPax) => {
  if (!pkg || !currentPax) return 0;

  // Check if package has new pricing_tiers structure (snake_case from API)
  if (pkg.pricing_tiers && Array.isArray(pkg.pricing_tiers)) {
    // Sort tiers by PAX in descending order to find the highest applicable tier
    const sortedTiers = [...pkg.pricing_tiers].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    
    // Find the appropriate pricing tier for current PAX
    const matchingTier = sortedTiers.find(tier => 
      currentPax >= parseInt(tier.pax)
    );

    if (matchingTier && matchingTier.price) {
      return convertToNumberKWD(matchingTier.price);
    }

    // If no exact match, use the lowest tier (fallback to first tier)
    if (pkg.pricing_tiers.length > 0) {
      return convertToNumberKWD(pkg.pricing_tiers[0].price);
    }
  }

  // Also check for camelCase version (for backward compatibility)
  if (pkg.pricingTiers && Array.isArray(pkg.pricingTiers)) {
    // Sort tiers by PAX in descending order to find the highest applicable tier
    const sortedTiers = [...pkg.pricingTiers].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    
    // Find the appropriate pricing tier for current PAX
    const matchingTier = sortedTiers.find(tier => 
      currentPax >= parseInt(tier.pax)
    );

    if (matchingTier && matchingTier.price) {
      return convertToNumberKWD(matchingTier.price);
    }

    // If no exact match, use the lowest tier (fallback to first tier)
    if (pkg.pricingTiers.length > 0) {
      return convertToNumberKWD(pkg.pricingTiers[0].price);
    }
  }

  // Fallback to old structure for backward compatibility
  const paxRanges = [
    {
      from: pkg.pax_range_from,
      to: pkg.pax_range_to,
      price: pkg.pax_price
    },
    {
      from: pkg.pax_range_from2,
      to: pkg.pax_range_to2,
      price: pkg.pax_price2
    },
    {
      from: pkg.pax_range_from3,
      to: pkg.pax_range_to3,
      price: pkg.pax_price3
    },
    {
      from: pkg.pax_range_from4,
      to: pkg.pax_range_to4,
      price: pkg.pax_price4
    },
    {
      from: pkg.pax_range_from5,
      to: pkg.pax_range_to5,
      price: pkg.pax_price5
    }
  ].filter(range => range.from && range.to && range.price); // Filter out empty ranges

  // Find the appropriate price range for current PAX
  for (const range of paxRanges) {
    if (currentPax >= range.from && currentPax <= range.to) {
      return convertToNumberKWD(range.price);
    }
  }

  // If no range matches, return 0 (or you could return the first range price as fallback)
  return 0;
};

/**
 * Calculate total package base prices
 * @param {Array} packages - Array of packages
 * @param {number} currentPax - Current PAX count
 * @returns {number} Total base price of all packages
 */
export const calculateTotalPackageBasePrice = (packages, currentPax) => {
  if (!packages || packages.length === 0) return 0;

  return packages.reduce((total, pkg) => {
    // First try the new PAX-based pricing
    const paxBasedPrice = calculatePackagePrice(pkg, currentPax);
    if (paxBasedPrice > 0) {
      return total + paxBasedPrice;
    }
    
    // Fallback to old basePrice field if it exists
    const basePrice = typeof pkg.basePrice === 'number' ? pkg.basePrice : 0;
    return total + basePrice;
  }, 0);
};

/**
 * Calculate total display price for a specific item/variant quantity in the context of its package and category
 * @param {Object} item - The menu item
 * @param {Object} variantItem - The variant item (if applicable)
 * @param {Object} pkg - The package containing this item
 * @param {number} currentPax - Current PAX count
 * @returns {number} Total display price for this item's quantity (includes both free and chargeable portions)
 */
export const calculateItemDisplayPriceInPackage = (item, variantItem, pkg, currentPax) => {
  const categoryLimit = pkg.categories?.find(category => category.id === item.category_id);
  
  // Use getCateringItemPrice for catering items, or calculateVariantItemPrice for variant items
  const unitPrice = variantItem ? calculateVariantItemPrice(item, variantItem) : getCateringItemPrice(item, currentPax);
  const itemQuantity = variantItem ? variantItem.quantity : item.quantity;
  
  if (!categoryLimit || !categoryLimit.choose_rule || categoryLimit.choose_rule.length === 0) {
    // No limit defined for this category, show full price
    return unitPrice * itemQuantity;
  }

  // Find the appropriate choose rule for current PAX
  // Sort rules by pax descending and find the first one where currentPax >= rule.pax
  const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
  const matchingRule = sortedRules.find(rule => 
    currentPax >= parseInt(rule.pax)
  );
  
  if (!matchingRule || matchingRule.choose <= 0) {
    // No matching rule or no item limit, show full price
    return unitPrice * itemQuantity;
  }

  // Build a list of all items in this category using addOrderHistory for temporal ordering
  const itemsInSameCategory = pkg.items?.filter(itm => itm.category_id === item.category_id) || [];
  const allCategoryItemsWithOrder = [];

  itemsInSameCategory.forEach(categoryItem => {
    if (categoryItem?.has_variants) {
      categoryItem.items?.forEach(vItem => {
        const vPrice = calculateVariantItemPrice(categoryItem, vItem);
        const addOrderHistory = vItem.addOrderHistory || 
          Array.from({ length: vItem.quantity || 0 }, (_, i) => i);
        
        addOrderHistory.forEach(order => {
          allCategoryItemsWithOrder.push({
            price: vPrice,
            itemId: categoryItem.id,
            variantId: vItem.variantId,
            order,
            // Match based on item ID, variant ID, AND price to distinguish items with different add-ons
            isCurrentItem: categoryItem.id === item.id && 
                          (variantItem ? (vItem.variantId === variantItem.variantId && vPrice === unitPrice) : false)
          });
        });
      });
    } else {
      // Use getCateringItemPrice which handles both add_on_options and regular price
      const itemPrice = getCateringItemPrice(categoryItem, currentPax);
      const addOrderHistory = categoryItem.addOrderHistory || 
        Array.from({ length: categoryItem.quantity || 0 }, (_, i) => i);
      
      addOrderHistory.forEach(order => {
        allCategoryItemsWithOrder.push({
          price: itemPrice,
          itemId: categoryItem.id,
          variantId: null,
          order,
          // Match based on item ID AND price to distinguish items with different add-ons
          isCurrentItem: categoryItem.id === item.id && !variantItem && itemPrice === unitPrice
        });
      });
    }
  });

  // Sort by add order (temporal sequence) to maintain the actual addition order
  allCategoryItemsWithOrder.sort((a, b) => a.order - b.order);
  const allCategoryItems = allCategoryItemsWithOrder;

  const maxFreeItemsForCategory = matchingRule.choose;
  const totalCategoryQuantity = allCategoryItems.length;
  
  // If total category quantity is within the free limit, all items are free
  if (totalCategoryQuantity <= maxFreeItemsForCategory) {
    return 0;
  }

  // Count how many instances of the current item are free vs chargeable
  let freeInstancesOfCurrentItem = 0;
  let chargeableInstancesOfCurrentItem = 0;
  
  for (let i = 0; i < allCategoryItems.length; i++) {
    const categoryItem = allCategoryItems[i];
    
    if (categoryItem.isCurrentItem) {
      if (i < maxFreeItemsForCategory) {
        // This instance is within the free limit (cheapest items are free)
        freeInstancesOfCurrentItem++;
      } else {
        // This instance is chargeable (most expensive items are charged)
        chargeableInstancesOfCurrentItem++;
      }
    }
  }

  console.log('Individual item pricing debug:', {
    itemName: item.name || item.name_en,
    variantId: variantItem?.variantId,
    categoryId: item.category_id,
    unitPrice: unitPrice,
    itemQuantity: itemQuantity,
    totalCategoryQuantity,
    maxFreeItemsForCategory,
    freeInstancesOfCurrentItem,
    chargeableInstancesOfCurrentItem,
    calculatedPrice: chargeableInstancesOfCurrentItem * unitPrice
  });

  // Return the price only for chargeable instances
  return chargeableInstancesOfCurrentItem * unitPrice;
};

/**
 * Get selected options string for display
 * @param {Array} selectedVariant - Array of selected variant options
 * @returns {string} Formatted options string
 */
export const getSelectedOptions = (selectedVariant) => {
  return selectedVariant
    .flatMap((variant) =>
      variant.options
        .filter((opt) => opt.option_quantity > 0)
        .map((opt) => `${opt.option_name} (${opt.option_quantity})`)
    )
    .join(", ");
};

/**
 * Transform catering packages data into the API payload format for modify order
 * @param {Object} params - Parameters for the transformation
 * @param {Array} params.packages - Array of catering packages
 * @param {Object} params.filters - Filters containing pax, date, time
 * @returns {Object} API payload for modify catering order
 */
export const transformModifyOrderPayload = ({
  packages,
  filters
}) => {
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Transform items to the catering service items format for modify order
  const cateringServiceItems = packages.flatMap(pkg => {
    if (!pkg.items || !Array.isArray(pkg.items)) return [];
    
    // Build a map of items by category to track order and determine which are chargeable
    const itemsByCategory = {};
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      
      if (item.has_variants && item.items) {
        item.items.forEach(variantItem => {
          const addOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          
          addOrderHistory.forEach(order => {
            const itemPrice = calculateVariantItemPrice(item, variantItem);
            itemsByCategory[categoryId].push({ order, price: itemPrice });
          });
        });
      } else {
        const addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
        
        addOrderHistory.forEach(order => {
          const itemPrice = getCateringItemPrice(item, filters.pax);
          itemsByCategory[categoryId].push({ order, price: itemPrice });
        });
      }
    });
    
    // For each category, determine which items are chargeable
    const chargeableMap = {}; // Map to track which items at which order positions are chargeable
    
    Object.keys(itemsByCategory).forEach(categoryId => {
      const categoryItems = itemsByCategory[categoryId];
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      
      let maxFreeItems = 0;
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => filters.pax >= parseInt(rule.pax));
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItems = matchingRule.choose;
        }
      }
      
      // Sort items by order (temporal sequence)
      categoryItems.sort((a, b) => a.order - b.order);
      
      // Items beyond maxFreeItems are chargeable
      chargeableMap[categoryId] = new Set(
        categoryItems.slice(maxFreeItems).map(item => item.order)
      );
    });
    
    return pkg.items.map(item => {
      // Find the category for this item
      const categoryId = item.category_id || item.catering_category_id || "1";
      
      // Find the category limit to get the correct "choose" value
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      let chooseValue = "1"; // Default fallback
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        // Find the appropriate choose rule for current PAX
        // Sort rules by pax descending and find the first one where currentPax >= rule.pax
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          filters.pax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          chooseValue = matchingRule.choose.toString();
        }
      }
      
      // Determine portion based on portion_by_pax array
      const getPortionForPax = (item, currentPax) => {
        if (item.portion_by_pax && Array.isArray(item.portion_by_pax) && item.portion_by_pax.length > 0) {
          // Sort by pax descending to find the highest applicable tier
          const sortedPortions = [...item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
          
          // Find the appropriate portion for current PAX
          const matchingPortion = sortedPortions.find(p => currentPax >= parseInt(p.pax));
          
          if (matchingPortion && matchingPortion.portions) {
            return matchingPortion.portions.toString();
          }
          
          // If no match, use the lowest tier (first in original array)
          if (item.portion_by_pax[0] && item.portion_by_pax[0].portions) {
            return item.portion_by_pax[0].portions.toString();
          }
        }
        
        // Default fallback
        return "10";
      };
      
      const portionValue = getPortionForPax(item, filters.pax);
      
      if (item.has_variants && item.items) {
        // For variant items, create entries for each variant
        return item.items.map(variantItem => {
          const itemPrice = calculateVariantItemPrice(item, variantItem);
          const addOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          
          // Calculate add_on_price: sum of prices for chargeable instances only
          let totalAddOnPrice = 0;
          addOrderHistory.forEach(order => {
            if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
              totalAddOnPrice += itemPrice;
            }
          });
          
          return {
            catering_category_id: categoryId.toString(),
            catering_item_id: item.id.toString(),
            quantity: variantItem.quantity?.toString() || "1",
            choose: chooseValue,
            portion: portionValue,
            no_of_pax: filters.pax?.toString() || "20",
            add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "0.000",
            currency: "KWD"
          };
        });
      } else {
        // For non-variant items
        const itemPrice = getCateringItemPrice(item, filters.pax);
        const addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
        
        // Calculate add_on_price: sum of prices for chargeable instances only
        let totalAddOnPrice = 0;
        addOrderHistory.forEach(order => {
          if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
            totalAddOnPrice += itemPrice;
          }
        });
        
        return {
          catering_category_id: categoryId.toString(),
          catering_item_id: item.id.toString(),
          quantity: item.quantity?.toString() || "1",
          choose: chooseValue,
          portion: portionValue,
          no_of_pax: filters.pax?.toString() || "20",
          add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "0.000",
          currency: "KWD"
        };
      }
    });
  }).flat();

  return {
    service_item_date: formatDate(filters.date),
    service_item_slot: filters.time || "10:30 PM - 11:00 PM",
    catering_service_items: cateringServiceItems
  };
};